/* eslint-disable */
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Col, Grid, Row } from 'react-bootstrap';
import qs from 'qs';
import axios from 'axios';

import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { delay, getErrorMessage, pageWrapperScrollTop } from '../../../utils/helper';
import { getReportsUsersConfig, getReportsUsersTable } from '../../../services/endpoints/reports';
import { getLastSubstr, mergeFilter } from './helpers';
import { getReportsConfigAction } from '../../../redux/actions/reports';

import Loader from '../../../components/Loaders/Loader';
import AlertComponent from '../../../components/Alert';
import Table from './components/Table';
import Pagination from './components/Pagination';
import Menu from '../components/Menu';
import Filters from './components/Filters';
import ReportsLoader from '../components/Loader';

export const ReportContext = createContext({});
export const useReports = () => useContext(ReportContext);

const ReportsUsers = () => {
  const location = useLocation();
  const dispatch = useDispatch();

  const reports = useSelector((state) => state.reports);
  const isLoadedMainConfig = !isEmpty(reports.mainConfig);

  const fakePageSize = 100;

  const cancelSourceRef = useRef(null);
  const cancelConfigRef = useRef(null);
  const timeoutRef = useRef(null);

  const queryString = qs.parse(location.search.slice(1));

  const [init, setInit] = useState(false);
  const [error, setError] = useState(null);

  const [isLoading, setLoading] = useState(false);
  const [fakeLoading, setFakeLoading] = useState(false);

  const [config, setConfig] = useState({});
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({});
  const [stack, setStack] = useState(queryString?.stack || 1);
  const [filters, setFilters] = useState({})

  const fetchList = async (queryParams, queue) => {
    try {
      if (cancelSourceRef.current) {
        cancelSourceRef.current.cancel('Request canceled');
      }

      const source = axios.CancelToken.source();
      cancelSourceRef.current = source;

      const payload = {
        ...queryParams,
        hash: queue.hash,
      };
      const response = await getReportsUsersTable(payload, source.token);

      if (response?.data.status === 'error') {
        throw response?.data;
      }

      if (response?.data.status === 'processing') {
        return new Promise((resolve, reject) => {
          const timeoutId = setTimeout(async () => {
            try {
              const result = await fetchList(queryParams, queue);
              resolve(result);
            } catch (error) {
              reject(error);
            }
          }, queue.request_interval * 1000);

          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          timeoutRef.current = timeoutId;
        });
      }

      if (response?.data.status === 'completed') {
        return response?.data;
      }
    } catch (error) {
      if (axios.isCancel(error)) throw error;
      throw error;
    }
  };

  const fetchData = async () => {
    let isCanceled = false;
    try {
      setLoading(true);

      [cancelConfigRef, cancelSourceRef].forEach((ref) => {
        if (ref.current) {
          ref.current.cancel('Request canceled');
          ref.current = null;
        }
      });
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      const configSource = axios.CancelToken.source();
      cancelConfigRef.current = configSource;

      const [respUserConfig] = await Promise.all([
        getReportsUsersConfig(queryString, configSource.token),
      ]);

      const { queue, filters, ...respConfig } = respUserConfig.data;
      const responseList = await fetchList(queryString, queue);

      setData(responseList.data.reports);
      setPagination(responseList.pagination);
      setConfig({
        ...respConfig,
        sources: mergeFilter(respConfig.sources, responseList.data.sources),
      });
      setFilters(filters);
      setError(null);
    } catch (error) {
      if (axios.isCancel(error)) {
        isCanceled = true;
        return;
      }
      setError(getErrorMessage(error));
    } finally {
      if (!isCanceled) {
        await delay(300);
        setLoading(false);
      }
      setInit(true)
    }
  };

  useEffect(() => {
    if (isLoadedMainConfig) {
      fetchData();
    }
    document.title = 'Users';

    return () => {
      if (cancelConfigRef.current) {
        cancelConfigRef.current.cancel();
      }
      if (cancelSourceRef.current) {
        cancelSourceRef.current.cancel();
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    queryString.query,
    queryString.page,
    queryString.sort,
    queryString.query,
    queryString.range,
    queryString.date_from,
    queryString.date_to,
    isLoadedMainConfig,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(queryString.source),
  ]);

  useEffect(() => {
    if (init) {
      pageWrapperScrollTop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString.page, queryString.stack]);

  const fetchFakeData = async () => {
    setFakeLoading(true);
    let newStack = 1;
    if (queryString.hasOwnProperty('stack')) {
      newStack = Number(queryString.stack);
    }
    setStack(newStack);
    await delay(300)
    setFakeLoading(false)
  }

  useEffect(() => {
    fetchFakeData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString.stack]);

  useEffect(() => {
    if (!isLoadedMainConfig) {
      dispatch(getReportsConfigAction());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fakeData = useMemo(() => {
    if (fakePageSize > pagination.total_count) return data;

    const start = (stack - 1) * fakePageSize;
    const end = start + fakePageSize;

    const startData = getLastSubstr(start) === '000' ? 0 : parseInt(getLastSubstr(start), 10);
    const endData = getLastSubstr(end) === '000' ? 1000 : parseInt(getLastSubstr(end), 10);

    return data.slice(Math.max(startData, 0), Math.min(endData, data.length));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, pagination, stack, isLoading, fakeLoading]);

  const contextValue = {
    pagination,
    config,
    isLoading: isLoading || fakeLoading,
    filters,
    data: fakeData,
  }

  if (!isLoadedMainConfig) {
    return <Loader />
  }

  return (
    <ReportContext.Provider value={contextValue}>
      <Grid fluid>
        <Row>
          <Col md={12}>
            <div className="reports-filters mb-15">
              <Menu />
              {init ? <Filters /> : null}
            </div>
          </Col>
        </Row>
        {init
          ? (
            <>
              {error ? <AlertComponent type="danger">{error}</AlertComponent> : null}
              <Table />
              <Pagination
                stack={stack}
                fakePageSize={fakePageSize}
              />
            </>
          ) : <ReportsLoader />}
      </Grid>
    </ReportContext.Provider>
  );
};

export default ReportsUsers;
